// @ts-nocheck
import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';

const MembershipSuccess: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshProfile, profile } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [verified, setVerified] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [pollMessage, setPollMessage] = useState('Verifying your subscription...');

  const sessionId = searchParams.get('session_id');
  const membershipSlug = searchParams.get('membership_slug');

  // Check if membership is a press addon
  const isPressAddon = membershipSlug?.startsWith('press_');

  const pollForMembership = async (maxAttempts = 15, intervalMs = 2000) => {
    if (!profile?.id || !membershipSlug) {
      console.log('❌ [MEMBERSHIP-SUCCESS] Cannot poll for membership: missing profile ID or membership slug', {
        profileId: profile?.id,
        membershipSlug,
        timestamp: new Date().toISOString()
      });
      return false;
    }

    console.log('🔍 [MEMBERSHIP-SUCCESS] Starting to poll for membership:', {
      profileId: profile.id,
      membershipSlug,
      maxAttempts,
      intervalMs,
      timestamp: new Date().toISOString()
    });
    
    setIsPolling(true);
    setPollMessage('Activating your press access...');

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      console.log(`🔄 [MEMBERSHIP-SUCCESS] Poll attempt ${attempt}/${maxAttempts} for membership ${membershipSlug}`, {
        profileId: profile.id,
        membershipSlug,
        attempt,
        timestamp: new Date().toISOString()
      });
      
      try {
        const { data: profileMemberships, error } = await supabase
          .from('profile_memberships')
          .select('membership_slug, is_active, assigned_at')
          .eq('profile_id', profile.id)
          .eq('membership_slug', membershipSlug)
          .eq('is_active', true);

        if (error) {
          console.error('❌ [MEMBERSHIP-SUCCESS] Error polling for membership:', {
            attempt,
            error,
            profileId: profile.id,
            membershipSlug,
            timestamp: new Date().toISOString()
          });
          continue;
        }

        console.log(`📊 [MEMBERSHIP-SUCCESS] Poll attempt ${attempt} results:`, {
          profileId: profile.id,
          membershipSlug,
          foundMemberships: profileMemberships?.length || 0,
          memberships: profileMemberships,
          timestamp: new Date().toISOString()
        });

        if (profileMemberships && profileMemberships.length > 0) {
          console.log('✅ [MEMBERSHIP-SUCCESS] Membership found during polling:', {
            attempt,
            membership: profileMemberships[0],
            profileId: profile.id,
            membershipSlug,
            timestamp: new Date().toISOString()
          });
          setIsPolling(false);
          return true;
        }

        console.log(`⏳ [MEMBERSHIP-SUCCESS] Membership not found yet, waiting... (${attempt}/${maxAttempts})`, {
          profileId: profile.id,
          membershipSlug,
          timestamp: new Date().toISOString()
        });
        
        if (attempt < maxAttempts) {
          await new Promise(resolve => setTimeout(resolve, intervalMs));
        }
      } catch (error) {
        console.error('💥 [MEMBERSHIP-SUCCESS] Error during polling attempt:', {
          attempt,
          error,
          profileId: profile.id,
          membershipSlug,
          timestamp: new Date().toISOString()
        });
      }
    }

    setIsPolling(false);
    console.log('⚠️ [MEMBERSHIP-SUCCESS] Polling completed without finding membership:', {
      profileId: profile.id,
      membershipSlug,
      totalAttempts: maxAttempts,
      timestamp: new Date().toISOString()
    });
    return false;
  };

  useEffect(() => {
    // ✅ LOG: On Stripe return (success/cancel)
    console.log("🎉 [MEMBERSHIP-SUCCESS] MembershipSuccess page loaded", {
      sessionId,
      membershipSlug,
      isPressAddon,
      profileId: profile?.id,
      profileRole: profile?.role,
      currentUrl: window.location.href,
      searchParams: Object.fromEntries(searchParams.entries()),
      timestamp: new Date().toISOString()
    });

    const handleSuccess = async () => {
      if (sessionId && membershipSlug) {
        try {
          console.log("🔄 [MEMBERSHIP-SUCCESS] Refreshing profile after successful checkout...", {
            sessionId,
            membershipSlug,
            profileId: profile?.id,
            timestamp: new Date().toISOString()
          });
          
          // Refresh the user profile to get updated membership info
          await refreshProfile();
          
          // ✅ LOG: Profile membership data refresh status
          console.log("📋 [MEMBERSHIP-SUCCESS] Profile refreshed, checking membership data:", {
            profileId: profile?.id,
            profileRole: profile?.role,
            membershipSlug,
            isPressAddon,
            timestamp: new Date().toISOString()
          });
          
          // If it's a press addon, poll for the membership record
          if (isPressAddon && profile?.id) {
            console.log("📰 [MEMBERSHIP-SUCCESS] Processing press addon success", {
              membershipSlug,
              profileId: profile.id,
              startingPolling: true,
              timestamp: new Date().toISOString()
            });
            
            const membershipFound = await pollForMembership();
            
            if (membershipFound) {
              console.log("✅ [MEMBERSHIP-SUCCESS] Press membership found, redirecting to parent dashboard", {
                membershipSlug,
                profileId: profile.id,
                redirecting: true,
                timestamp: new Date().toISOString()
              });
              setPollMessage('Press access activated! Redirecting...');
              setVerified(true);
              
              // Redirect to parent dashboard press tab after a short delay
              setTimeout(() => {
                navigate('/parent-dashboard?tab=press');
              }, 2000);
            } else {
              console.log("⚠️ [MEMBERSHIP-SUCCESS] Press membership not found after polling, but marking as verified", {
                membershipSlug,
                profileId: profile.id,
                pollingFailed: true,
                timestamp: new Date().toISOString()
              });
              setPollMessage('Press access pending. You can check again in a moment.');
              setVerified(true);
              
              // Still redirect to dashboard as fallback
              setTimeout(() => {
                navigate('/parent-dashboard?tab=press');
              }, 4000);
            }
          } else {
            // For non-press memberships, use existing logic
            console.log("🎯 [MEMBERSHIP-SUCCESS] Processing non-press membership success", {
              membershipSlug,
              profileId: profile?.id,
              profileRole: profile?.role,
              timestamp: new Date().toISOString()
            });
            setVerified(true);
            setTimeout(() => {
              if (profile?.role === 'parent') {
                navigate('/parent-dashboard');
              } else {
                navigate('/athlete-dashboard');
              }
            }, 4000);
          }
        } catch (error) {
          console.error('💥 [MEMBERSHIP-SUCCESS] Error processing success:', {
            error,
            errorMessage: error instanceof Error ? error.message : 'Unknown error',
            sessionId,
            membershipSlug,
            profileId: profile?.id,
            timestamp: new Date().toISOString()
          });
          // Still mark as verified since we got here from Stripe
          setVerified(true);
          setTimeout(() => {
            if (profile?.role === 'parent') {
              navigate('/parent-dashboard');
            } else {
              navigate('/athlete-dashboard');
            }
          }, 4000);
        }
      } else {
        console.log("⚠️ [MEMBERSHIP-SUCCESS] Missing session ID or membership slug", {
          sessionId,
          membershipSlug,
          searchParams: Object.fromEntries(searchParams.entries()),
          timestamp: new Date().toISOString()
        });
      }
      
      setIsLoading(false);
    };

    handleSuccess();
  }, [sessionId, membershipSlug, refreshProfile, navigate, isPressAddon, profile?.id, profile?.role]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-secondary-600" />
          <p className="text-gray-600">{pollMessage}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50">
      <Card className="max-w-md mx-4">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <CheckCircle className="h-16 w-16 text-green-500" />
          </div>
          <CardTitle className="text-2xl text-green-600">
            {isPressAddon ? '📰 Press Access Activated!' : '🎉 Upgrade Successful!'}
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center">
          {membershipSlug && (
            <p className="text-gray-600 mb-4">
              You're now on the <strong className="text-secondary-600">{membershipSlug.replace('_', ' ')}</strong> plan.
            </p>
          )}
          
          {isPolling ? (
            <div className="space-y-4">
              <Loader2 className="h-6 w-6 animate-spin mx-auto text-secondary-600" />
              <p className="text-sm text-gray-500">{pollMessage}</p>
            </div>
          ) : (
            <>
              <p className="text-sm text-gray-500 mb-6">
                {isPressAddon 
                  ? 'Your press access is now active. Redirecting to press subscriptions...'
                  : 'Your premium features are now unlocked. Redirecting to your dashboard...'
                }
              </p>
              
              <div className="space-y-2">
                <Button 
                  onClick={() => {
                    console.log('🔘 [MEMBERSHIP-SUCCESS] Manual navigation button clicked:', {
                      isPressAddon,
                      profileRole: profile?.role,
                      membershipSlug,
                      timestamp: new Date().toISOString()
                    });
                    if (isPressAddon) {
                      navigate('/parent-dashboard?tab=press');
                    } else if (profile?.role === 'parent') {
                      navigate('/parent-dashboard');
                    } else {
                      navigate('/athlete-dashboard');
                    }
                  }} 
                  className="w-full bg-secondary-600 hover:bg-secondary-700"
                >
                  {isPressAddon ? 'Go to Press Subscriptions' : 'Go to Dashboard Now'}
                </Button>
                
                <p className="text-xs text-gray-400">
                  Auto-redirecting in a few seconds
                </p>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MembershipSuccess;
